'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  image: string;
  alt: string;
  description: string;
  ingredients: string[];
  benefits: string[];
  seasonal: boolean;
}

interface ProductGalleryProps {
  onAddToCart: (productId: string) => void;
}

const ProductGallery = ({ onAddToCart }: ProductGalleryProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const products: Product[] = [
  {
    id: 'p1',
    name: 'Mango Pickle',
    category: 'pickles',
    price: 299,
    image: "/assets/images/mango-pickle-1766907651361.jpg",
    alt: 'Traditional Indian mango pickle in glass jar with golden mustard oil and red chili spices',
    description: 'Authentic raw mango pickle with traditional spices',
    ingredients: ['Raw Mango', 'Mustard Oil', 'Fenugreek', 'Red Chili', 'Turmeric'],
    benefits: ['Rich in Vitamin C', 'Aids Digestion', 'Boosts Immunity'],
    seasonal: true
  },
  {
    id: 'p2',
    name: 'Mixed Vegetable Pickle',
    category: 'pickles',
    price: 249,
    image: "/assets/images/Mixed-Vegetable-Pickle-1766908363007.jpg",
    alt: 'Colorful mixed vegetable pickle with carrots, cauliflower and green chilies in traditional glass jar',
    description: 'Farm-fresh vegetables pickled with aromatic spices',
    ingredients: ['Carrot', 'Cauliflower', 'Green Chili', 'Mustard Seeds', 'Sesame Oil'],
    benefits: ['High Fiber', 'Probiotic Rich', 'Antioxidants'],
    seasonal: false
  },
  {
    id: 'p3',
    name: 'Lemon Pickle',
    category: 'pickles',
    price: 199,
    image: "/assets/images/lemon-pickle-1766909294465.jpg",
    alt: 'Tangy lemon pickle with whole lemons in golden oil with black mustard seeds in glass container',
    description: 'Tangy lemon pickle with perfect spice balance',
    ingredients: ['Fresh Lemon', 'Rock Salt', 'Black Pepper', 'Mustard Oil', 'Asafoetida'],
    benefits: ['Vitamin C Boost', 'Digestive Aid', 'Natural Detox'],
    seasonal: false
  },
  {
    id: 'p4',
    name: 'Pure Deshi Ghee',
    category: 'ghee',
    price: 899,
    image: "/assets/images/deshi-ghee-1766909394511.jpg",
    alt: 'Golden pure deshi ghee in traditional brass container with wooden spoon on rustic wooden surface',
    description: 'Hand-churned from A2 cow milk using traditional methods',
    ingredients: ['A2 Cow Milk', 'Traditional Bilona Method'],
    benefits: ['Healthy Fats', 'Improves Digestion', 'Boosts Immunity'],
    seasonal: false
  },
  {
    id: 'p5',
    name: 'Amla Murraba',
    category: 'murrabas',
    price: 349,
    image: "/assets/images/amla-murraba-1766909831703.png",
    alt: 'Sweet amla murraba with whole Indian gooseberries in sugar syrup in decorative glass jar',
    description: 'Sweet amla preserve rich in Vitamin C',
    ingredients: ['Fresh Amla', 'Organic Jaggery', 'Cardamom', 'Saffron'],
    benefits: ['Immunity Booster', 'Hair Health', 'Skin Glow'],
    seasonal: true
  },
  {
    id: 'p6',
    name: 'Carrot Murraba',
    category: 'murrabas',
    price: 299,
    image: "/assets/images/carrot-murraba-1766909916911.png",
    alt: 'Orange carrot murraba pieces in sweet syrup with cardamom in traditional glass container',
    description: 'Seasonal carrot preserve with aromatic spices',
    ingredients: ['Red Carrots', 'Pure Honey', 'Cardamom', 'Almonds'],
    benefits: ['Eye Health', 'Antioxidants', 'Natural Energy'],
    seasonal: true
  }];


  const categories = [
  { id: 'all', label: 'All Products', icon: 'Squares2X2Icon' },
  { id: 'pickles', label: 'Pickles', icon: 'BeakerIcon' },
  { id: 'ghee', label: 'Deshi Ghee', icon: 'SparklesIcon' },
  { id: 'murrabas', label: 'Murrabas', icon: 'CakeIcon' }];


  const filteredProducts =
  selectedCategory === 'all' ?
  products :
  products.filter((p) => p.category === selectedCategory);

  const handleQuantityChange = (productId: string, delta: number) => {
    if (!isHydrated) return;
    setQuantities((prev) => ({
      ...prev,
      [productId]: Math.max(1, (prev[productId] || 1) + delta)
    }));
  };

  const handleAddToCart = (productId: string) => {
    if (!isHydrated) return;
    onAddToCart(productId);
  };

  return (
    <section id="products" className="bg-card py-16 lg:py-24">
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="mb-12 text-center">
          <h2 className="font-heading mb-4 text-3xl font-bold text-foreground lg:text-4xl">
            Our <span className="text-primary">Premium Collection</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Handcrafted with love using traditional recipes and farm-fresh ingredients
          </p>
        </div>

        <div className="mb-8 flex flex-wrap justify-center gap-3">
          {categories.map((category) =>
          <button
            key={category.id}
            onClick={() => isHydrated && setSelectedCategory(category.id)}
            className={`flex items-center gap-2 rounded-lg px-6 py-3 font-cta text-sm font-bold transition-all duration-250 ease-out ${
            (isHydrated ? selectedCategory : 'all') === category.id ?
            'bg-primary text-primary-foreground shadow-cta' : 'bg-background text-foreground hover:bg-muted'}`
            }>

              <Icon name={category.icon as any} size={20} />
              {category.label}
            </button>
          )}
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {filteredProducts.map((product) =>
          <div
            key={product.id}
            className="group relative overflow-hidden rounded-2xl bg-background shadow-cta transition-all duration-250 ease-out hover:shadow-lg">

              {product.seasonal &&
            <div className="absolute right-4 top-4 z-10 rounded-full bg-warning px-3 py-1 text-xs font-bold text-warning-foreground">
                  Seasonal
                </div>
            }

              <div className="relative h-64 overflow-hidden bg-muted">
                <AppImage
                src={product.image}
                alt={product.alt}
                className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-110" />

              </div>

              <div className="p-6">
                <div className="mb-2 flex items-start justify-between">
                  <h3 className="font-heading text-xl font-bold text-foreground">
                    {product.name}
                  </h3>
                  <span className="font-heading text-xl font-bold text-primary">
                    ₹{product.price}
                  </span>
                </div>

                <p className="mb-4 text-sm text-muted-foreground">
                  {product.description}
                </p>

                <div className="mb-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <Icon
                    name="SparklesIcon"
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-primary" />

                    <p className="text-xs text-muted-foreground">
                      {product.ingredients.join(', ')}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Icon
                    name="HeartIcon"
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-success" />

                    <p className="text-xs text-muted-foreground">
                      {product.benefits.join(' • ')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center rounded-lg border-2 border-border">
                    <button
                    onClick={() => handleQuantityChange(product.id, -1)}
                    className="px-3 py-2 text-foreground transition-colors duration-250 ease-out hover:bg-muted"
                    aria-label="Decrease quantity">

                      <Icon name="MinusIcon" size={16} />
                    </button>
                    <span className="min-w-[2rem] text-center font-bold text-foreground">
                      {isHydrated ? quantities[product.id] || 1 : 1}
                    </span>
                    <button
                    onClick={() => handleQuantityChange(product.id, 1)}
                    className="px-3 py-2 text-foreground transition-colors duration-250 ease-out hover:bg-muted"
                    aria-label="Increase quantity">

                      <Icon name="PlusIcon" size={16} />
                    </button>
                  </div>

                  <button
                  onClick={() => handleAddToCart(product.id)}
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-cta text-sm font-bold text-primary-foreground transition-all duration-250 ease-out hover:shadow-cta">

                    <Icon name="ShoppingCartIcon" size={18} />
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default ProductGallery;